<template>
    <component
        :is="tag"
        v-bind="tagProps"
        :class="classes"
        :style="customStyle"
        v-on="$listeners"
    >
        <span
            v-if="loading"
            :class="[
                'inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin',
                hasSlot ? 'mr-1.5' : ''
            ]"
        ></span>
        <slot></slot>
    </component>
</template>

<script>
const FILLED = {
    "brand-blue": "bg-brand-blue text-white hover:opacity-90",
    "brand-red": "bg-brand-red text-white hover:opacity-90",
    "brand-magenta": "bg-brand-magenta text-white hover:opacity-90",
    "brand-purple": "bg-brand-purple text-white hover:opacity-90",
    "brand-yellow": "bg-brand-yellow text-white hover:opacity-90",
    "brand-light": "bg-brand-light text-white hover:opacity-90",
    "stack-primary": "bg-stack-primary text-white hover:opacity-90",
    "stack-secondary": "bg-stack-secondary text-white hover:opacity-90",
    "stack-emphasis": "bg-stack-emphasis text-white hover:opacity-90",
    "stack-details": "bg-stack-details text-white hover:opacity-90",
    success: "bg-theme-success text-white hover:opacity-90",
    danger: "bg-theme-danger text-white hover:opacity-90",
    warn: "bg-theme-warning text-white hover:opacity-90",
    warning: "bg-theme-warning text-black hover:opacity-90"
};

const RELIEF = {
    "brand-blue":
        "border border-brand-blue text-brand-blue bg-transparent hover:bg-brand-blue hover:text-white",
    "brand-red":
        "border border-brand-red text-brand-red bg-transparent hover:bg-brand-red hover:text-white",
    "brand-magenta":
        "border border-brand-magenta text-brand-magenta bg-transparent hover:bg-brand-magenta hover:text-white",
    "brand-purple":
        "border border-brand-purple text-brand-purple bg-transparent hover:bg-brand-purple hover:text-white",
    "brand-yellow":
        "border border-brand-yellow text-brand-yellow bg-transparent hover:bg-brand-yellow hover:text-white",
    "brand-light":
        "border border-brand-light text-brand-light bg-transparent hover:bg-brand-light hover:text-white",
    "stack-primary":
        "border border-stack-primary text-stack-primary bg-transparent hover:bg-stack-primary hover:text-white",
    "stack-secondary":
        "border border-stack-secondary text-stack-secondary bg-transparent hover:bg-stack-secondary hover:text-white",
    "stack-emphasis":
        "border border-stack-emphasis text-stack-emphasis bg-transparent hover:bg-stack-emphasis hover:text-white",
    "stack-details":
        "border border-stack-details text-stack-details bg-transparent hover:bg-stack-details hover:text-white",
    success:
        "border border-theme-success text-theme-success bg-transparent hover:bg-theme-success hover:text-white",
    danger: "border border-theme-danger text-theme-danger bg-transparent hover:bg-theme-danger hover:text-white",
    warn: "border border-theme-warning text-theme-warning bg-transparent hover:bg-theme-warning hover:text-white",
    warning:
        "border border-theme-warning text-theme-warning bg-transparent hover:bg-theme-warning hover:text-white"
};

const FLAT = {
    "brand-blue": "text-brand-blue hover:bg-theme-hover",
    "brand-red": "text-brand-red hover:bg-theme-hover",
    "brand-magenta": "text-brand-magenta hover:bg-theme-hover",
    "brand-purple": "text-brand-purple hover:bg-theme-hover",
    "brand-yellow": "text-brand-yellow hover:bg-theme-hover",
    "brand-light": "text-brand-light hover:bg-theme-hover",
    "stack-primary": "text-stack-primary hover:bg-theme-hover",
    "stack-secondary": "text-stack-secondary hover:bg-theme-hover",
    "stack-emphasis": "text-stack-emphasis hover:bg-theme-hover",
    "stack-details": "text-stack-details hover:bg-theme-hover",
    success: "text-theme-success hover:bg-theme-hover",
    danger: "text-theme-danger hover:bg-theme-hover",
    warn: "text-theme-warning hover:bg-theme-hover",
    warning: "text-theme-warning hover:bg-theme-hover"
};

export default {
    name: "AppButton",
    props: {
        color: { type: String, default: null },
        danger: { type: Boolean, default: false },
        success: { type: Boolean, default: false },
        relief: { type: Boolean, default: false },
        border: { type: Boolean, default: false },
        flat: { type: Boolean, default: false },
        circle: { type: Boolean, default: false },
        icon: { type: Boolean, default: false },
        block: { type: Boolean, default: false },
        size: { type: String, default: null },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        to: { type: [String, Object], default: null },
        href: { type: String, default: null },
        type: { type: String, default: "button" }
    },
    computed: {
        hasSlot() {
            return !!this.$slots.default;
        },
        tag() {
            if (this.to) return "router-link";
            if (this.href) return "a";
            return "button";
        },
        tagProps() {
            if (this.to) return { to: this.to };
            if (this.href) return { href: this.href };
            return {
                type: this.type,
                disabled: this.isDisabled || undefined
            };
        },
        isDisabled() {
            return this.disabled || this.loading;
        },
        resolvedColor() {
            if (this.danger) return "danger";
            if (this.success) return "success";
            return this.color || "brand-magenta";
        },
        isHex() {
            return this.resolvedColor.startsWith("#");
        },
        customStyle() {
            if (!this.isHex) return {};
            const c = this.resolvedColor;
            if (this.flat) return { color: c };
            if (this.relief || this.border) return { borderColor: c, color: c };
            return { backgroundColor: c, color: "#fff" };
        },
        classes() {
            const base = [
                "inline-flex items-center justify-center font-medium",
                "transition-all duration-200 select-none",
                "focus:outline-none",
                "mx-0.5"
            ];

            if (this.isDisabled) {
                base.push("opacity-50 cursor-not-allowed pointer-events-none");
            } else {
                base.push("cursor-pointer");
            }

            if (this.block) base.push("w-full");

            base.push(this.circle ? "rounded-full" : "rounded-md");

            if (this.icon) {
                const s = { large: "w-12 h-12", small: "w-6 h-6" };
                base.push(s[this.size] || "w-10 h-10");
            } else {
                const s = {
                    large: "px-6 py-2.5 text-base",
                    small: "px-2 py-1 text-xs"
                };
                base.push(s[this.size] || "px-4 py-2 text-sm");
            }

            if (!this.isHex) {
                const c = this.resolvedColor;
                if (this.flat) {
                    base.push(FLAT[c] || FLAT["brand-magenta"]);
                } else if (this.relief || this.border) {
                    base.push(RELIEF[c] || RELIEF["brand-magenta"]);
                } else {
                    base.push(FILLED[c] || FILLED["brand-magenta"]);
                }
            } else if (this.flat) {
                base.push("bg-transparent");
            } else if (this.relief || this.border) {
                base.push("border bg-transparent");
            }

            return base;
        }
    }
};
</script>
